import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Settings2, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { confirmDialog } from '@/lib/confirmDialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  CLASE_ESTADO,
  ETIQUETA_ESTADO,
} from '../constants/presupuestos';
import { PRESUPUESTOS_ROUTES, rutaDetallePresupuesto } from '../constants/routes';
import { usePlantillas } from '../hooks/usePlantillas';
import { usePresupuestosProforma } from '../hooks/usePresupuestosProforma';
import { usePresupuestosProformaPermissions } from '../hooks/usePresupuestosProformaPermissions';
import { formatearFecha, formatearMonto } from '../utils/formatters';
import { BotonDesasociarPresupuesto } from './BotonDesasociarPresupuesto';
import { EnlaceEscritura } from './EnlaceEscritura';
import type { EstadoPresupuestoProforma, PresupuestosProformaFiltros } from '../types';

const TODOS = 'todos';
const SIN_PLANTILLA = 'ninguna';

export const PresupuestosListPage: React.FC = () => {
  const navigate = useNavigate();
  const permisos = usePresupuestosProformaPermissions();

  const [filtros, setFiltros] = useState<PresupuestosProformaFiltros>({});
  const [modalNuevo, setModalNuevo] = useState(false);
  const [nombreNuevo, setNombreNuevo] = useState('');
  const [errorNuevo, setErrorNuevo] = useState('');
  const [plantillaNueva, setPlantillaNueva] = useState(SIN_PLANTILLA);

  const { presupuestos, isLoading, crearPresupuesto, eliminarPresupuesto, isCreando } =
    usePresupuestosProforma(filtros);
  const { plantillas, isLoading: isCargandoPlantillas } = usePlantillas({
    soloActivas: true,
    enabled: modalNuevo,
  });

  const setFiltro = (campo: keyof PresupuestosProformaFiltros, valor: unknown) =>
    setFiltros((actual) => ({ ...actual, [campo]: valor, page: 1 }));

  const handleCrear = async () => {
    const nombre = nombreNuevo.trim();

    if (!nombre) {
      setErrorNuevo('El nombre del presupuesto es obligatorio');
      return;
    }

    try {
      const creado = await crearPresupuesto({
        nombre,
        plantilla_id: plantillaNueva === SIN_PLANTILLA ? null : plantillaNueva,
      });
      setModalNuevo(false);
      setNombreNuevo('');
      setPlantillaNueva(SIN_PLANTILLA);
      navigate(rutaDetallePresupuesto(creado.id));
    } catch {
      setErrorNuevo('No se pudo crear el presupuesto. Intente nuevamente.');
    }
  };

  const handleEliminar = async (id: string, nombre: string) => {
    const confirmado = await confirmDialog.open({
      title: `¿Eliminar el presupuesto "${nombre}"?`,
      text: 'Esta acción no se puede deshacer.',
      confirmButtonText: 'Eliminar',
    });

    if (!confirmado) return;

    try {
      await eliminarPresupuesto(id);
      toast.success(`Se eliminó "${nombre}".`);
    } catch (error: any) {
      toast.error(error?.response?.data?.message ?? 'No se pudo eliminar el presupuesto.');
    }
  };

  return (
    <div className="space-y-6 p-2 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">Presupuestos</h1>
        <div className="flex items-center gap-2">
          {permisos.canManageBases && (
            <Button variant="outline" asChild>
              <Link to={PRESUPUESTOS_ROUTES.PLANTILLAS}>
                <Settings2 className="mr-2 h-4 w-4" />
                Plantillas
              </Link>
            </Button>
          )}
          {permisos.canCreate && (
            <Button onClick={() => setModalNuevo(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Nuevo presupuesto
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="grid gap-4 py-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="space-y-1.5">
            <Label htmlFor="filtro-nombre">Buscar por nombre</Label>
            <Input
              id="filtro-nombre"
              value={filtros.search ?? ''}
              onChange={(event) => setFiltro('search', event.target.value || undefined)}
              placeholder="Nombre del presupuesto"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Estado</Label>
            <Select
              value={filtros.estado ?? TODOS}
              onValueChange={(valor) =>
                setFiltro('estado', valor === TODOS ? undefined : (valor as EstadoPresupuestoProforma))
              }
            >
              <SelectTrigger aria-label="Filtrar por estado">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TODOS}>Todos (sin archivados)</SelectItem>
                <SelectItem value="borrador">Borrador</SelectItem>
                <SelectItem value="confirmado">Confirmado</SelectItem>
                <SelectItem value="importado">Importado</SelectItem>
                <SelectItem value="archivado">Archivado</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="filtro-desde">Desde</Label>
              <Input
                id="filtro-desde"
                type="date"
                value={filtros.desde ?? ''}
                onChange={(event) => setFiltro('desde', event.target.value || undefined)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="filtro-hasta">Hasta</Label>
              <Input
                id="filtro-hasta"
                type="date"
                value={filtros.hasta ?? ''}
                onChange={(event) => setFiltro('hasta', event.target.value || undefined)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Importados</Label>
            <Select
              value={filtros.importado === undefined ? TODOS : String(filtros.importado)}
              onValueChange={(valor) =>
                setFiltro('importado', valor === TODOS ? undefined : valor === 'true')
              }
            >
              <SelectTrigger aria-label="Filtrar por importados">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TODOS}>Todos</SelectItem>
                <SelectItem value="true">Importados</SelectItem>
                <SelectItem value="false">No importados</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Creado</TableHead>
                <TableHead>Modificado</TableHead>
                <TableHead className="text-right">Total adquiriente</TableHead>
                <TableHead className="text-right">Total transferente</TableHead>
                <TableHead>Importado</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className="py-8 text-center text-muted-foreground">
                    Cargando presupuestos…
                  </TableCell>
                </TableRow>
              ) : presupuestos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="py-8 text-center text-muted-foreground">
                    No hay presupuestos que coincidan con los filtros.
                  </TableCell>
                </TableRow>
              ) : (
                presupuestos.map((presupuesto) => (
                  <TableRow key={presupuesto.id}>
                    <TableCell className="font-medium">
                      <Link
                        to={rutaDetallePresupuesto(presupuesto.id)}
                        className="block max-w-[260px] truncate underline-offset-4 hover:underline"
                        title={presupuesto.nombre}
                      >
                        {presupuesto.nombre}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Badge className={CLASE_ESTADO[presupuesto.estado]} variant="secondary">
                        {ETIQUETA_ESTADO[presupuesto.estado]}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatearFecha(presupuesto.createdAt)}</TableCell>
                    <TableCell>{formatearFecha(presupuesto.updatedAt)}</TableCell>
                    <TableCell className="text-right">
                      {formatearMonto(presupuesto.totales.adquiriente.ARS, 'ARS')}
                      {presupuesto.totales.adquiriente.USD > 0 && (
                        <div className="text-xs text-muted-foreground">
                          {formatearMonto(presupuesto.totales.adquiriente.USD, 'USD')}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatearMonto(presupuesto.totales.transferente.ARS, 'ARS')}
                      {presupuesto.totales.transferente.USD > 0 && (
                        <div className="text-xs text-muted-foreground">
                          {formatearMonto(presupuesto.totales.transferente.USD, 'USD')}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {presupuesto.importadoEn.length === 0 ? (
                        <span className="text-muted-foreground">No</span>
                      ) : (
                        <div className="flex flex-col gap-0.5">
                          {presupuesto.importadoEn.map((escritura) => (
                            <div key={escritura.tramiteId} className="flex items-center gap-1">
                              <EnlaceEscritura escritura={escritura} className="text-sm" />
                              {permisos.canImport && (
                                <BotonDesasociarPresupuesto
                                  tramiteId={escritura.tramiteId}
                                  nombrePresupuesto={presupuesto.nombre}
                                  escritura={`#${escritura.tramiteId}`}
                                  variante="icono"
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {permisos.canDelete && !presupuesto.fueImportado && (
                        <Button
                          size="icon"
                          variant="ghost"
                          title="Eliminar presupuesto"
                          aria-label={`Eliminar presupuesto ${presupuesto.nombre}`}
                          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                          onClick={() =>
                            void handleEliminar(presupuesto.id, presupuesto.nombre)
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={modalNuevo} onOpenChange={(abierto) => !abierto && setModalNuevo(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuevo presupuesto</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <Label htmlFor="nombre-presupuesto">Nombre *</Label>
            <Input
              id="nombre-presupuesto"
              value={nombreNuevo}
              maxLength={150}
              onChange={(event) => {
                setNombreNuevo(event.target.value);
                setErrorNuevo('');
              }}
              placeholder="Ej: Venta Chacras de Coria"
            />
            {errorNuevo && <p className="text-sm text-destructive">{errorNuevo}</p>}
          </div>
          <div className="space-y-2 pb-4">
            <Label htmlFor="plantilla-presupuesto">Plantilla</Label>
            <Select value={plantillaNueva} onValueChange={setPlantillaNueva}>
              <SelectTrigger id="plantilla-presupuesto" aria-label="Plantilla del presupuesto">
                <SelectValue placeholder={isCargandoPlantillas ? 'Cargando…' : undefined} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SIN_PLANTILLA}>Sin plantilla (vacío)</SelectItem>
                {plantillas.map((plantilla) => (
                  <SelectItem key={plantilla.id} value={plantilla.id}>
                    {plantilla.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Se crearán las cajas del adquiriente y del transferente con los ítems de la
              plantilla elegida. También podés importar una plantilla después.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalNuevo(false)} disabled={isCreando}>
              Cancelar
            </Button>
            <Button onClick={() => void handleCrear()} disabled={isCreando}>
              Crear
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
